// Grid geometry from a puzzle document: cells in core order (row-major over existing cells),
// regions per cell, peers, and the edges where region borders are drawn.
import type { PuzzleDoc, Rc } from '@zinc/shared';

export interface Geometry {
  rows: number;
  cols: number;
  /** Cells in core order. */
  cells: Rc[];
  /** Canvas position → cell index (or -1). */
  index: Int32Array;
  /** Region index per cell (-1 when in none). */
  region: Int32Array;
  /** Houses (rows, columns, regions) as cell lists, for highlights and peers. */
  houses: number[][];
  peers: number[][];
  digits: string[];
  givens: (string | null)[];
  solution: string[] | null;
  /** The fog, when the puzzle has one. */
  fog: PuzzleDoc['fog'];
}

export function cellAt(g: Geometry, r: number, c: number): number {
  if (r < 0 || c < 0 || r >= g.rows || c >= g.cols) return -1;
  return g.index[r * g.cols + c]!;
}

export function buildGeometry(doc: PuzzleDoc): Geometry {
  const { rows, cols } = doc.grid;
  const present = new Uint8Array(rows * cols);
  if (doc.grid.cells) {
    for (const [r, c] of doc.grid.cells) present[r * cols + c] = 1;
  } else {
    present.fill(1);
  }
  const index = new Int32Array(rows * cols).fill(-1);
  const cells: Rc[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (present[r * cols + c]) {
        index[r * cols + c] = cells.length;
        cells.push([r, c]);
      }
    }
  }
  const g: Geometry = {
    rows,
    cols,
    cells,
    index,
    region: new Int32Array(cells.length).fill(-1),
    houses: [],
    peers: [],
    digits: doc.grid.digits,
    givens: new Array<string | null>(cells.length).fill(null),
    solution: doc.solution && doc.solution.length === cells.length ? doc.solution : null,
    fog: doc.fog ?? null,
  };
  const subs = doc.grid.subGrids ?? [
    { row: 0, col: 0, rows, cols, boxRows: doc.grid.boxRows, boxCols: doc.grid.boxCols },
  ];
  for (const s of subs) {
    for (let r = s.row; r < s.row + s.rows; r++) {
      g.houses.push(
        range(s.col, s.col + s.cols)
          .map((c) => cellAt(g, r, c))
          .filter((i) => i >= 0),
      );
    }
    for (let c = s.col; c < s.col + s.cols; c++) {
      g.houses.push(
        range(s.row, s.row + s.rows)
          .map((r) => cellAt(g, r, c))
          .filter((i) => i >= 0),
      );
    }
  }
  const regions: number[][] = [];
  if (doc.grid.regions) {
    for (const reg of doc.grid.regions) {
      regions.push(reg.map(([r, c]) => cellAt(g, r, c)).filter((i) => i >= 0));
    }
  } else {
    for (const s of subs) {
      if (s.boxRows <= 0 || s.boxCols <= 0) continue;
      for (let br = s.row; br < s.row + s.rows; br += s.boxRows) {
        for (let bc = s.col; bc < s.col + s.cols; bc += s.boxCols) {
          const reg: number[] = [];
          for (let r = br; r < Math.min(br + s.boxRows, s.row + s.rows); r++) {
            for (let c = bc; c < Math.min(bc + s.boxCols, s.col + s.cols); c++) {
              const i = cellAt(g, r, c);
              if (i >= 0) reg.push(i);
            }
          }
          if (reg.length > 0) regions.push(reg);
        }
      }
    }
  }
  const seen = new Set<string>();
  const memberships = new Int32Array(cells.length);
  for (const reg of regions) {
    const key = [...reg].sort((a, b) => a - b).join(',');
    if (seen.has(key)) continue;
    seen.add(key);
    for (const i of reg) {
      g.region[i] = seen.size - 1;
      memberships[i]! += 1;
    }
    g.houses.push(reg);
  }
  // Boxes that overlap (Chutney's squishdoku) can't be drawn as borders between regions: only
  // the grid's outline is, and the setter's own lines show the boxes.
  if (memberships.some((k) => k > 1)) g.region.fill(0);
  const peerSets = cells.map(() => new Set<number>());
  for (const h of g.houses) {
    for (const a of h) for (const b of h) if (a !== b) peerSets[a]!.add(b);
  }
  g.peers = peerSets.map((s) => [...s]);
  for (const given of doc.givens) {
    const i = cellAt(g, given.cell[0], given.cell[1]);
    if (i >= 0) g.givens[i] = given.value;
  }
  return g;
}

function range(from: number, to: number): number[] {
  return Array.from({ length: to - from }, (_, i) => from + i);
}

/** Region borders: segments between cells of different regions, and around the grid edge. */
export function regionEdges(g: Geometry): { r1: number; c1: number; r2: number; c2: number }[] {
  const edges: { r1: number; c1: number; r2: number; c2: number }[] = [];
  const regionAt = (r: number, c: number) => {
    const i = cellAt(g, r, c);
    return i < 0 ? -2 : g.region[i]!;
  };
  for (let r = 0; r <= g.rows; r++) {
    for (let c = 0; c < g.cols; c++) {
      const above = regionAt(r - 1, c);
      const below = regionAt(r, c);
      if (above !== below && (above !== -2 || below !== -2))
        edges.push({ r1: r, c1: c, r2: r, c2: c + 1 });
    }
  }
  for (let c = 0; c <= g.cols; c++) {
    for (let r = 0; r < g.rows; r++) {
      const left = regionAt(r, c - 1);
      const right = regionAt(r, c);
      if (left !== right && (left !== -2 || right !== -2))
        edges.push({ r1: r, c1: c, r2: r + 1, c2: c });
    }
  }
  return edges;
}
